// Экран «Подрядчики» (решение владельца 2026-09-14): справочник подрядчиков проекта —
// название, контактное лицо, телефон, почта, комментарий и счётчики (задач, работ,
// корпусов, пользователей). Правка в строке по клику — как в «Работах»/«Ресурсах»
// (общая `EditCell`), строка добавления в конце таблицы, удаление с подтверждением
// (занятого — задачами или пользователями — сервер не удаляет, 409 in_use).
// Переименование — с подтверждением каскада: сервер одной транзакцией переписывает
// имя в задачах и у участников, подрядчик после этого не теряет свои работы.
// Клик по названию — карточка (`.dlg.wc`): поля, сводная «работы × корпуса» по задачам
// подрядчика (в клетке — задач, в скобках окончено) и привязанные пользователи.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { call, p, type Data, type Req } from '../api';
import { alertBox, confirmBox } from '../dialog';
import type { ShellCtx } from '../shell';
import { EditCell } from './Works';

type Podr = Data<'/api/projects/{pid}/podryadchiki', 'get'>[number];
type Card = Data<'/api/podryadchiki/{pdid}/card', 'get'>;
type Telo = Req<'/api/podryadchiki/{pdid}', 'patch'>;
type Pole = 'kontakt' | 'telefon' | 'email' | 'kommentarij';
type Otvet = { id: number; rev: number; zadach: number; polzovatelej: number };

const POLYA: { kod: Pole; imya: string; podskazka: string }[] = [
  { kod: 'kontakt', imya: 'Контактное лицо', podskazka: 'Кто на стороне подрядчика отвечает за объект' },
  { kod: 'telefon', imya: 'Телефон', podskazka: 'Телефон контактного лица' },
  { kod: 'email', imya: 'Почта', podskazka: 'Почта контактного лица; проверяется только вид адреса' },
  { kod: 'kommentarij', imya: 'Комментарий', podskazka: 'Заметка планера: договор, особенности, что помнить' },
];

const url = (pdid: number) => p('/api/podryadchiki/{pdid}', { pdid });

/** сообщение после переименования: сколько задач и пользователей переписано */
function kaskadText(d: Otvet) {
  return d.zadach || d.polzovatelej
    ? ': название обновлено в задачах — ' + d.zadach + ', у пользователей — ' + d.polzovatelej + '.'
    : '.';
}

/** окно подтверждения переименования, когда подрядчик уже назначен; true — переименовывать */
async function podtverditPereimenovanie(staroe: string, novoe: string, zadach: number, polzovatelej: number) {
  if (!zadach && !polzovatelej) return true;
  return confirmBox('Переименовать «' + staroe + '» в «' + novoe + '»?', {
    ok: 'Переименовать',
    podpis: 'Название поменяется в задачах: ' + zadach + ', у пользователей: ' + polzovatelej
      + '. Подрядчик не потеряет свои работы; журналы и история не меняются.' });
}

export function Podryad({ ctx }: { ctx: ShellCtx }) {
  const [rows, setRows] = useState<Podr[]>([]);
  const [msg, setMsg] = useState('');
  const [adding, setAdding] = useState(false);
  const [card, setCard] = useState<number | null>(null);
  const can = ctx.canPlan;

  const load = useCallback(async () => {
    const r = await call<Podr[]>('get', p('/api/projects/{pid}/podryadchiki', { pid: ctx.pid }));
    if (!r.ok) { setMsg(r.error.message); return; }
    setRows(r.data);
    // reloadToken — сигнал оболочки «данные изменились» (карточка, другие экраны)
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  // выпадающие списки подрядчика на других экранах читают «Списки» при своей загрузке —
  // оболочке достаточно нового reloadToken (он же перечитает и этот экран)
  const changed = (m = '') => { setMsg(m); ctx.onError?.(''); if (ctx.onChanged) ctx.onChanged(); else void load(); };

  async function patch(x: Podr, body: Telo) {
    const r = await call<Otvet>('patch', url(x.id), { ...body, rev: x.rev });
    if (!r.ok) { fail(r.error.message); if (r.error.code === 'stale') void load(); return null; }
    return r.data;
  }
  async function pravit(x: Podr, kod: Pole, v: string) {
    const d = await patch(x, { [kod]: v.trim() || null });
    if (d) changed('');
  }
  async function pereimenovat(x: Podr, novoe: string) {
    const v = novoe.trim();
    if (!v || v === x.name) return;
    if (!await podtverditPereimenovanie(x.name, v, x.zadach, x.polzovatelej)) return;
    const d = await patch(x, { name: v });
    if (d) changed('Подрядчик «' + x.name + '» переименован в «' + v + '»' + kaskadText(d));
  }
  async function udalit(x: Podr) {
    const i = await call<{ zadach: number; polzovatelej: number }>('delete', url(x.id) + '?info=1');
    if (!i.ok) { fail(i.error.message); return; }
    if (i.data.zadach || i.data.polzovatelej) {
      await alertBox('Подрядчика «' + x.name + '» удалить нельзя',
        'Он назначен: задач — ' + i.data.zadach + ', пользователей — ' + i.data.polzovatelej
        + '. Переназначьте их (в модели, шахматке или на «Пользователях») либо переименуйте подрядчика.');
      return;
    }
    if (!await confirmBox('Удалить подрядчика «' + x.name + '»?', {
      ok: 'Удалить', opasno: true,
      podpis: 'Задач и пользователей с ним нет — удаляется только строка справочника.' })) return;
    const r = await call('delete', url(x.id));
    if (!r.ok) { await alertBox(r.error.message); void load(); return; }   // 409 in_use — назначили за это время
    changed('Подрядчик «' + x.name + '» удалён.');
  }

  return (
    <div>
      <div className="bar actbar">
        {can && <button className="sec" title="Добавить подрядчика: строка ввода появится внизу таблицы"
          onClick={() => setAdding(true)}>+ подрядчик</button>}
        <span className="grow" />
        <span className="pw-muted">{msg}</span>
      </div>
      <div className="tbl"><table>
        <thead>
          <tr>
            <th title="Клик по названию — карточка: работы по корпусам и пользователи. Переименовать — значок ✎ в строке или поле в карточке">Название</th>
            {POLYA.map(f => <th key={f.kod} title={f.podskazka + '. Клик по ячейке — правка'}>{f.imya}</th>)}
            <th className="num" title="Задач проекта, назначенных этому подрядчику">Задач</th>
            <th className="num" title="Работ, в которых у него есть задачи">Работ</th>
            <th className="num" title="Корпусов, где у него есть задачи">Корпусов</th>
            <th className="num" title="Пользователей с ролью «подрядчик», привязанных к нему (экран «Пользователи»)">Пользов.</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(x => (
            <tr key={x.id}>
              <NameCell x={x} can={can} onCard={() => setCard(x.id)} onRename={v => pereimenovat(x, v)} />
              {POLYA.map(f => (
                <EditCell key={f.kod} value={x[f.kod] || ''} can={can} title={f.podskazka}
                  onSave={async v => { await pravit(x, f.kod, v); }} />
              ))}
              <td className="num">{x.zadach || ''}</td>
              <td className="num">{x.rabot || ''}</td>
              <td className="num">{x.korpusov || ''}</td>
              <td className="num">{x.polzovatelej || ''}</td>
              <td className="num">{can && <button className="del"
                title="Удалить подрядчика — только если у него нет задач и пользователей (с подтверждением)"
                onClick={() => void udalit(x)}>×</button>}</td>
            </tr>
          ))}
          {!rows.length && !adding && (
            <tr><td colSpan={99} className="muted">Подрядчиков пока нет. Они появляются здесь сами, когда
              подрядчика назначают задаче или пользователю, либо добавьте вручную — «+ подрядчик».</td></tr>
          )}
          {can && adding && (
            <AddRow pid={ctx.pid} onDone={m => { setAdding(false); changed(m); }} onFail={fail}
              onCancel={() => setAdding(false)} />
          )}
        </tbody>
      </table></div>
      {card != null && (
        <PodrCard pdid={card} can={can} onClose={() => setCard(null)}
          onChanged={m => changed(m)} />
      )}
    </div>
  );
}

/** название: клик — карточка, значок ✎ — переименовать прямо в строке */
function NameCell({ x, can, onCard, onRename }:
  { x: Podr; can: boolean; onCard: () => void; onRename: (v: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(x.name);
  useEffect(() => { setV(x.name); }, [x.name]);
  if (editing) {
    const done = async (save: boolean) => {
      setEditing(false);
      if (save && v.trim() && v.trim() !== x.name) await onRename(v); else setV(x.name);
    };
    return (
      <td>
        <input autoFocus value={v} style={{ width: '100%' }} onChange={e => setV(e.target.value)}
          onBlur={() => void done(true)}
          onKeyDown={e => { if (e.key === 'Enter') void done(true); if (e.key === 'Escape') void done(false); }} />
      </td>
    );
  }
  return (
    <td>
      <span className="pw-link" title="Открыть карточку подрядчика: работы по корпусам, пользователи" onClick={onCard}>{x.name}</span>
      {can && <a className="tagadd" style={{ marginLeft: 6 }} title="Переименовать (название поменяется и в задачах, и у пользователей)"
        onClick={() => setEditing(true)}>✎</a>}
    </td>
  );
}

function AddRow({ pid, onDone, onFail, onCancel }: {
  pid: number; onDone: (m: string) => void; onFail: (m: string) => void; onCancel: () => void;
}) {
  const [f, setF] = useState({ name: '', kontakt: '', telefon: '', email: '', kommentarij: '' });
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({ ...s, [k]: v }));
  const klavishi = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); void add(); }
    if (e.key === 'Escape') onCancel();
  };
  async function add() {
    const name = f.name.trim();
    if (!name) { onFail('Введите название подрядчика'); return; }
    const body: Req<'/api/projects/{pid}/podryadchiki', 'post'> = {
      name, kontakt: f.kontakt.trim() || null, telefon: f.telefon.trim() || null,
      email: f.email.trim() || null, kommentarij: f.kommentarij.trim() || null };
    const r = await call<{ id: number }>('post', p('/api/projects/{pid}/podryadchiki', { pid }), body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Подрядчик «' + name + '» добавлен.');
  }
  return (
    <tr>
      {/* одна ячейка на всю строку — как строка «+ работа» в справочнике «Работы» */}
      <td colSpan={99}>
        <div className="bar" style={{ margin: 0 }}>
          <input autoFocus placeholder="Название подрядчика" style={{ width: 260, maxWidth: '30%' }} value={f.name}
            onChange={e => set('name')(e.target.value)} onKeyDown={klavishi} />
          <input placeholder="контактное лицо" title="Контактное лицо" value={f.kontakt} onChange={e => set('kontakt')(e.target.value)} onKeyDown={klavishi} />
          <input placeholder="телефон" title="Телефон" style={{ width: 140 }} value={f.telefon} onChange={e => set('telefon')(e.target.value)} onKeyDown={klavishi} />
          <input placeholder="почта" title="Почта — проверяется только вид адреса" style={{ width: 180 }} value={f.email} onChange={e => set('email')(e.target.value)} onKeyDown={klavishi} />
          <input placeholder="комментарий" title="Комментарий" value={f.kommentarij} onChange={e => set('kommentarij')(e.target.value)} onKeyDown={klavishi} />
          <button onClick={() => void add()}>Добавить</button>
          <button className="sec" onClick={onCancel}>Отмена</button>
          <span className="pw-muted">Enter — сохранить, Esc — отмена</span>
        </div>
      </td>
    </tr>
  );
}

/** Карточка подрядчика: поля с правкой, сводная «работы × корпуса», пользователи */
function PodrCard({ pdid, can, onClose, onChanged }:
  { pdid: number; can: boolean; onClose: () => void; onChanged: (m: string) => void }) {
  const [c, setC] = useState<Card | null>(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await call<Card>('get', p('/api/podryadchiki/{pdid}/card', { pdid }));
    if (!r.ok) { setErr(r.error.message); return; }
    setC(r.data); setErr('');
  }, [pdid]);
  useEffect(() => { void load(); }, [load]);

  const zadach = useMemo(() => (c?.raboty || []).reduce((s, r) => s + r.zadach, 0), [c]);
  // сводная: строки — работы в порядке сервера (по имени), столбцы — корпуса по buildings.num
  const svod = useMemo(() => {
    const m = new Map<string, Map<string, Card['raboty'][number]>>();
    for (const r of c?.raboty || []) {
      if (!m.has(r.rabota)) m.set(r.rabota, new Map());
      m.get(r.rabota)!.set(r.korpus, r);
    }
    return [...m.entries()];
  }, [c]);

  async function patch(pole: string, znachenie: string | null) {
    if (!c) return;
    // rev — версия строки, которую показала карточка: чужую правку не затираем (409 stale)
    const r = await call<Otvet>('patch', url(pdid), { [pole]: znachenie, rev: c.rev });
    if (!r.ok) { setErr(r.error.message); if (r.error.code === 'stale') await load(); return; }
    setErr('');
    await load();
    onChanged(pole === 'name' ? 'Подрядчик переименован в «' + znachenie + '»' + kaskadText(r.data) : '');
  }
  async function pereimenovat(v: string) {
    if (!c) return;
    const nv = v.trim();
    if (!nv || nv === c.name) return;
    if (!await podtverditPereimenovanie(c.name, nv, zadach, c.polzovateli.length)) { await load(); return; }
    await patch('name', nv);
  }

  const pole = (label: string, uzel: JSX.Element, hint?: string) => (
    <div className="wc-pole" key={label} title={hint}>
      <span className="wc-lab">{label}</span>
      {uzel}
    </div>
  );
  const vvod = (znach: string | null | undefined, na: string, onBlur = (v: string) => void patch(na, v || null)) => (
    <input defaultValue={znach ?? ''} disabled={!can} key={na + (c?.rev ?? 0)}
      onBlur={e => { const v = e.target.value.trim(); if (v !== String(znach ?? '')) onBlur(v); }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
  const kletka = (r?: Card['raboty'][number]) => r
    ? <td className="num" key={r.korpus} title={'задач ' + r.zadach + ' · начато ' + r.nachato + ' · окончено ' + r.zaversheno}>
        {r.zadach}{r.zaversheno ? ' (' + r.zaversheno + ')' : ''}</td>
    : <td className="num muted" key="-">—</td>;

  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dlg wc" role="dialog" aria-modal="true" onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
        <div className="wc-top">
          <div className="wc-name">{c ? c.name : 'Подрядчик'}</div>
          <a className="wc-x" title="Закрыть (Esc)" onClick={onClose}>✕</a>
        </div>
        {c && (
          <div className="wc-info">
            Задач: <b>{zadach}</b> · работ: <b>{svod.length}</b> · корпусов: <b>{c.korpusa.length}</b>
            {' '}· пользователей: <b>{c.polzovateli.length}</b>
          </div>
        )}
        <div className="wc-body">
          {err && <div className="err">{err}</div>}
          {!c && !err && <div className="muted">Загрузка…</div>}
          {c && (
            <>
              <div className="wc-polya">
                {pole('Название', vvod(c.name, 'name', v => void pereimenovat(v)),
                  'Переименование поменяет название и в задачах, и у пользователей — с подтверждением')}
                {POLYA.map(f => pole(f.imya, vvod(c[f.kod], f.kod), f.podskazka))}
              </div>
              <div className="wc-podzag" style={{ marginTop: 12 }}
                title="По задачам подрядчика: строки — работы, столбцы — корпуса; в клетке — задач, в скобках — окончено по факту">
                Работы по корпусам</div>
              {!svod.length && <div className="muted">Задач у подрядчика нет.</div>}
              {svod.length > 0 && (
                <div className="tbl" style={{ maxHeight: '40vh' }}><table>
                  <thead><tr>
                    <th>Работа</th>
                    {c.korpusa.map(k => <th key={k} className="num">{k}</th>)}
                    <th className="num" title="Задач по всем корпусам (в скобках — окончено)">Всего</th>
                  </tr></thead>
                  <tbody>
                    {svod.map(([rabota, poKorpusu]) => {
                      const vsego = [...poKorpusu.values()].reduce((s, r) => s + r.zadach, 0);
                      const zav = [...poKorpusu.values()].reduce((s, r) => s + r.zaversheno, 0);
                      return (
                        <tr key={rabota}>
                          <td>{rabota}</td>
                          {c.korpusa.map(k => kletka(poKorpusu.get(k)))}
                          <td className="num"><b>{vsego}</b>{zav ? ' (' + zav + ')' : ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              )}
              <div className="wc-podzag" style={{ marginTop: 12 }}
                title="Пользователи с ролью «подрядчик», привязанные к этой организации на экране «Пользователи»">
                Пользователи</div>
              {!c.polzovateli.length && <div className="muted">Пользователей с этим подрядчиком нет — привязка делается на «Пользователях».</div>}
              {c.polzovateli.length > 0 && (
                <div className="wc-list">
                  {c.polzovateli.map(u => (
                    <div className="wc-item" key={u.user_id}>
                      <div className="wc-item-t">
                        <div className="wc-item-n">{u.name || u.email}</div>
                        <div className="wc-item-d">{u.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
